/**
 * AI Assistant Panel (right sidebar) — Claude Sonnet 4.5 summary, alerts, suggestions.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Sparkles, RefreshCw, AlertTriangle, Loader2, Lightbulb, Wand2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const AIAssistantPanel = ({ clientId, initial, onRefreshed, t }) => {
  const [data, setData] = useState(initial || {});
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setData(initial || {}); }, [initial]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/clients/${clientId}/ai/summary`, {}, { withCredentials: true });
      setData(r.data);
      toast.success(t("AI aggiornata", "AI refreshed"));
      if (onRefreshed) onRefreshed();
    } catch (err) {
      const msg = err?.response?.data?.detail || t("Errore AI", "AI error");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const hasContent = data?.summary || (data?.alerts?.length > 0) || (data?.suggestions?.length > 0);

  return (
    <div className="rounded-3xl border border-violet-200/60 dark:border-violet-900/30 bg-gradient-to-br from-violet-50 via-white to-cyan-50 dark:from-violet-950/40 dark:via-slate-900 dark:to-cyan-950/40 p-4 space-y-3 sticky top-4" data-testid="client-ai-panel">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center shadow-md">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">AI Assistant</h3>
          <p className="text-[10px] text-muted-foreground">Claude Sonnet 4.5</p>
        </div>
        <Button
          size="sm" variant="ghost" className="h-7 w-7 p-0 rounded-full"
          onClick={generate} disabled={loading}
          data-testid="client-ai-refresh-btn"
          title={t("Rigenera", "Regenerate")}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {!hasContent && !loading && (
        <div className="rounded-xl bg-white dark:bg-slate-900/60 border border-violet-100 dark:border-violet-900/30 p-3 text-center">
          <Wand2 className="w-5 h-5 mx-auto text-violet-500 mb-1.5" />
          <p className="text-xs text-slate-700 dark:text-slate-300">
            {t("Genera un riassunto intelligente del cliente", "Generate an intelligent client summary")}
          </p>
          <Button
            size="sm" className="mt-2 rounded-full w-full bg-gradient-to-r from-violet-500 to-cyan-500 hover:from-violet-600 hover:to-cyan-600 text-white"
            onClick={generate} data-testid="client-ai-generate-btn"
          >
            <Sparkles className="w-3 h-3 mr-1.5" /> {t("Genera AI", "Generate AI")}
          </Button>
        </div>
      )}

      {loading && (
        <div className="rounded-xl bg-white dark:bg-slate-900/60 p-4 text-center">
          <Loader2 className="w-5 h-5 mx-auto text-violet-500 animate-spin mb-1.5" />
          <p className="text-[11px] text-muted-foreground">{t("Analisi in corso...", "Analyzing...")}</p>
        </div>
      )}

      {!loading && data.summary && (
        <div className="rounded-xl bg-white/80 dark:bg-slate-900/60 border border-violet-100 dark:border-violet-900/30 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-1.5">
            {t("Riassunto", "Summary")}
          </p>
          <p className="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{data.summary}</p>
        </div>
      )}

      {!loading && data.alerts?.length > 0 && (
        <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/30 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {t("Alert", "Alerts")}
          </p>
          <ul className="space-y-1">
            {data.alerts.map((a, i) => (
              <li key={i} className="text-xs text-slate-700 dark:text-slate-200">• {a}</li>
            ))}
          </ul>
        </div>
      )}

      {!loading && data.suggestions?.length > 0 && (
        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/30 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> {t("Suggerimenti", "Suggestions")}
          </p>
          <ul className="space-y-1">
            {data.suggestions.map((s, i) => (
              <li key={i} className="text-xs text-slate-700 dark:text-slate-200">→ {s}</li>
            ))}
          </ul>
        </div>
      )}

      {!loading && data.generated_at && (
        <p className="text-[9px] text-center text-muted-foreground">
          {t("Aggiornato", "Updated")}: {fmt(data.generated_at)}
        </p>
      )}
    </div>
  );
};

export default AIAssistantPanel;
